import { Component, OnInit, Input } from '@angular/core';
import { GungFlow } from './../../../../state/flow/types';
import { GungFlowService } from './../../../../services/gung-flow/gung-flow.service';
import { gungComparatorHelper } from '../../../../utils/gung-utils';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs';
import {
  humanizeConditionPath,
  isEmptyConditionGroup,
  joinConditionValues,
  splitConditionValues
} from './flow-product-condition-utils';

export type ConditionOperator = '_$in' | '_$nin';

interface Entry {
  key: ConditionOperator;
  value: any;
}

export interface Setting {
  index: number;
  key: string[];
  label: string;
  entry: Entry;
}

/**
 * Editor for GungFlow.productConditions.
 *
 * A flow's productConditions is a list of condition groups. A product is in the flow when at least one
 * group matches (OR between groups), and a group matches when every field rule in it matches (AND within
 * a group). An empty group matches every product. The editor mirrors that model: "rule groups" made of
 * "rules", where only the rules that are in use are shown.
 *
 * The data model is kept as one full Setting list per group (every known field, most with a null value)
 * because subclasses and customer templates rely on finding settings by label in flowProductConditions.
 * Which unused fields are shown is tracked separately per group in openedFields.
 */
@Component({
    selector: 'lib-gung-flow-details-product-settings',
    templateUrl: './gung-flow-details-product-settings.component.html',
    styleUrls: ['./gung-flow-details-product-settings.component.css'],
    standalone: false
})
export class GungFlowDetailsProductSettingsComponent implements OnInit {
  @Input()
  curentGungFlow: GungFlow;
  settings: Setting[] = [];
  flowProductConditions: Setting[][] = [];

  readonly operators: ConditionOperator[] = ['_$in', '_$nin'];
  readonly operatorLabels: Record<ConditionOperator, string> = {
    _$in: 'FLOW_CONDITION_IS_ONE_OF',
    _$nin: 'FLOW_CONDITION_IS_NOT_ONE_OF'
  };

  /** Per group: labels of fields the user opened via "Add rule" but which have no value yet. */
  protected openedFields: Set<string>[] = [];
  pickerFilter: string[] = [];
  protected pendingValue: Record<string, string> = {};

  private readonly INCLUDE_KEY: ConditionOperator = '_$in';
  private readonly EXCLUDE_KEY: ConditionOperator = '_$nin';

  constructor(
    protected gungFlowService: GungFlowService,
    protected translateService: TranslateService
  ) {}

  ngOnInit() {
    this.settings = [];
    this.gungFlowService.getSystemGung().pipe(first()).subscribe(systemGung => {

      if (!!this.curentGungFlow.productConditions && Array.isArray(this.curentGungFlow.productConditions)) {
        for (const [index, entry] of this.curentGungFlow.productConditions.entries()) {
          for (const key of Object.keys(this.curentGungFlow.productConditions[index])) {
            this.processNestedObjectFromFlow(this.curentGungFlow.productConditions[index][key], index, [key]);
          }
        }
      }

      if (systemGung.extra.flowProductConditions) {
        for (const key of Object.keys(systemGung.extra.flowProductConditions)) {
          this.processNestedObjectFromSystemSettings(systemGung.extra.flowProductConditions[key], [key]);
        }
      }
      this.settings = this.settings.sort((a, b) => gungComparatorHelper(this.translateService.instant(a.label), this.translateService.instant(b.label), 1));

      if(!this.curentGungFlow.productConditions){
        this.curentGungFlow.productConditions = [];
      }else{
        for (const conditions of this.curentGungFlow.productConditions) {
          const settings = structuredClone(this.settings)
          const settingsValues = this.populateValues(settings, conditions);
          this.flowProductConditions.push(settingsValues)
        }
      }

      this.openedFields = this.flowProductConditions.map(() => new Set<string>());
      this.pickerFilter = this.flowProductConditions.map(() => '');
      this.doAfterInit();
    });
  }

  doAfterInit() {
    // override this method for custom logic
  }

  protected afterGroupAdded(groupIndex: number): void {
    // override to set up per-group state (e.g. option lists) for a group added through add()
  }


  visibleSettings(groupIndex: number): Setting[] {
    const opened = this.openedFields[groupIndex];
    return (this.flowProductConditions[groupIndex] || []).filter(
      s => !!s.entry.value || opened?.has(s.label)
    );
  }

  availableSettings(groupIndex: number): Setting[] {
    const opened = this.openedFields[groupIndex];
    return (this.flowProductConditions[groupIndex] || []).filter(
      s => !s.entry.value && !opened?.has(s.label)
    );
  }

  filteredAvailableSettings(groupIndex: number): Setting[] {
    const filter = (this.pickerFilter[groupIndex] || '').trim().toLowerCase();
    const available = this.availableSettings(groupIndex);
    if (!filter) {
      return available;
    }
    return available.filter(
      s => this.displayName(s).toLowerCase().includes(filter) || s.label.toLowerCase().includes(filter)
    );
  }

  addRule(groupIndex: number, setting: Setting): void {
    this.openedFields[groupIndex].add(setting.label);
    this.pickerFilter[groupIndex] = '';
  }

  removeRule(groupIndex: number, setting: Setting): void {
    this.openedFields[groupIndex].delete(setting.label);
    delete this.pendingValue[this.pendingKey(groupIndex, setting)];
    this.setValue(null, setting, groupIndex);
  }

  isGroupEmpty(groupIndex: number): boolean {
    return isEmptyConditionGroup(this.flowProductConditions[groupIndex] || []);
  }

  /** Translated field name, falling back to the readable path when no translation exists. */
  displayName(setting: Setting): string {
    const translated = this.translateService.instant(setting.label);
    return translated && translated !== setting.label ? translated : humanizeConditionPath(setting.key);
  }


  valuesOf(setting: Setting): string[] {
    return splitConditionValues(setting.entry.value);
  }

  setValues(values: string[], setting: Setting, groupIndex: number): void {
    this.setValue(joinConditionValues(values), setting, groupIndex);
  }

  setOperator(operator: ConditionOperator, setting: Setting, groupIndex: number): void {
    this.keyChange(operator, setting, groupIndex);
  }

  addValues(rawText: string, setting: Setting, groupIndex: number): void {
    const incoming = splitConditionValues(rawText);
    if (incoming.length === 0) {
      return;
    }
    const current = this.valuesOf(setting);
    const merged = [...current, ...incoming.filter(v => !current.includes(v))];
    this.setValues(merged, setting, groupIndex);
  }

  removeValue(value: string, setting: Setting, groupIndex: number): void {
    this.setValues(this.valuesOf(setting).filter(v => v !== value), setting, groupIndex);
  }

  pendingKey(groupIndex: number, setting: Setting): string {
    return `${groupIndex}:${setting.label}`;
  }

  onValueInput(text: string, setting: Setting, groupIndex: number): void {
    this.pendingValue[this.pendingKey(groupIndex, setting)] = text;
  }

  onValueKeydown(event: KeyboardEvent, setting: Setting, groupIndex: number): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.commitPending(input, setting, groupIndex);
    } else if (event.key === 'Backspace' && input.value === '') {
      const values = this.valuesOf(setting);
      if (values.length > 0) {
        this.removeValue(values[values.length - 1], setting, groupIndex);
      }
    }
  }

  onValueBlur(event: FocusEvent, setting: Setting, groupIndex: number): void {
    this.commitPending(event.target as HTMLInputElement, setting, groupIndex);
  }

  onValuePaste(event: ClipboardEvent, setting: Setting, groupIndex: number): void {
    const text = event.clipboardData?.getData('text') || '';
    if (text.includes(',')) {
      event.preventDefault();
      this.addValues(text, setting, groupIndex);
      (event.target as HTMLInputElement).value = '';
      delete this.pendingValue[this.pendingKey(groupIndex, setting)];
    }
  }

  private commitPending(input: HTMLInputElement, setting: Setting, groupIndex: number): void {
    this.addValues(input.value, setting, groupIndex);
    input.value = '';
    delete this.pendingValue[this.pendingKey(groupIndex, setting)];
  }


  populateValues(settings, conditions) {
    return settings.map(item => {
        const [firstKey, ...remainingKeys] = item.key;
        let currentConditionLevel = conditions[firstKey];
        for (const key of remainingKeys) {
            if (currentConditionLevel && currentConditionLevel[key]) {
              currentConditionLevel = currentConditionLevel[key];
            } else {
                currentConditionLevel = null;
                break;
            }
        }
        if (currentConditionLevel) {
            const operator = Object.keys(currentConditionLevel)[0];
            if (operator) {
                const values = Object.values(currentConditionLevel[operator]);
                if (values.length > 0) {
                    item.entry.key = operator;
                    item.entry.value = values.join(',');
                }
            }
        }
        return item;
    });
}

processNestedObjectFromFlow(obj, index, key = []) {
  for (const nextMapKey of Object.keys(obj)) {
    const nextMap = obj[nextMapKey];
    const setting: Setting = {
      index,
      key: [...key, nextMapKey],
      label: ([...key, nextMapKey].join('_')).toUpperCase(),
      entry: { key: this.INCLUDE_KEY, value: null }
    };
    const existsSetting = this.settings.find(s => s.label === setting.label);
    if(existsSetting){
      continue;
    }
    if (nextMap.hasOwnProperty(this.INCLUDE_KEY) || nextMap.hasOwnProperty(this.EXCLUDE_KEY)) {
      this.settings.push(setting);
    } else if (typeof nextMap === 'object') {
      this.processNestedObjectFromFlow(nextMap, index, setting.key);
    } else {
      console.error('This should not happen');
    }
  }
}

protected getValue(value: any): Entry {
  return {
    key: Object.keys(value)[0] == this.EXCLUDE_KEY ? this.EXCLUDE_KEY : this.INCLUDE_KEY,
    value: Array.from(Object.values(value)[0] as any).join(',')
  };
}

  private processNestedObjectFromSystemSettings(obj, key = []) {
    if (typeof obj === 'string') {
      const label = key.join('_').toUpperCase();
      const pathNotAddedFromFlow = this.settings.findIndex(s => s.label === label) === -1;
      if (pathNotAddedFromFlow) {
        this.settings.push({
          index: 0,
          key: [...key],
          label,
          entry: { key: this.INCLUDE_KEY, value: null }
        });
      }
      return;
    }
    for (const nextMapKey of Object.keys(obj)) {
      const nextMap = obj[nextMapKey];
      const setting: Setting = {
        index: 0,
        key: [...key, nextMapKey],
        label: ([...key, nextMapKey].join('_')).toUpperCase(),
        entry: { key: this.INCLUDE_KEY, value: null }
      };
      const pathNotAddedFromFlow = this.settings.findIndex(s => s.label === setting.label) === -1;
      if (pathNotAddedFromFlow && typeof nextMap === 'string') {
        this.settings.push(setting);
      } else if (typeof nextMap === 'object') {
        this.processNestedObjectFromSystemSettings(nextMap, setting.key);
      }
    }
  }

  public delete(index: number) {
    this.curentGungFlow.productConditions.splice(index, 1);
    this.flowProductConditions.splice(index, 1);
    this.openedFields.splice(index, 1);
    this.pickerFilter.splice(index, 1);
  }


  setValue(value: any, setting: Setting, index): void {
    setting.entry.value = value;
    this.assignValuesToFlow(this.flowProductConditions[index], index);
  }

  keyChange(key: any, setting: Setting, index) {
    const oldVal = setting.entry.value;
    setting.entry.key = key;
    this.setValue(oldVal, setting, index);
  }

  assignValuesToFlow(settingsConditions, index){
    const dateToSave = this.mapDataToSave(settingsConditions);
    this.curentGungFlow.productConditions[index] = dateToSave;
  }

  mapDataToSave(input): any {
    let result = {};

    input.forEach(item => {
      if (item.entry.value) {
        const valuesArray = item.entry.value.split(',').map(value => value.trim());

        let nestedObject: any = {};
        let currentLevel = nestedObject;
        item.key.forEach((key: string, index: number) => {
          if (index === item.key.length - 1) {
            currentLevel[key] = { [item.entry.key]: valuesArray };
          } else {
            currentLevel[key] = currentLevel[key] || {};
            currentLevel = currentLevel[key];
          }
        });
        result = this.mergeDeep(result, nestedObject);
      }
    });

    return result;
  }

  mergeDeep(target: any, source: any) {
    for (const key of Object.keys(source)) {
      if (Array.isArray(source[key])) {
        target[key] = source[key];
      } else if (source[key] instanceof Object) {
        if (!target[key]) {
          Object.assign(target, { [key]: {} });
        }
        this.mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
    return target;
  }

  public add(){
    // Each group needs its own Setting objects; sharing the master list would make groups edit each other.
    this.flowProductConditions.push(structuredClone(this.settings));
    this.curentGungFlow.productConditions.push({});
    this.openedFields.push(new Set<string>());
    this.pickerFilter.push('');
    this.afterGroupAdded(this.flowProductConditions.length - 1);
  }
}
